import React, { Component } from "react";
import Row from "reactstrap/lib/Row";
import Col from "reactstrap/lib/Col";
import { Button as RSButton, Collapse as RSCollapse, Card, CardBody } from 'reactstrap';
import { Table, TableHead, TableBody, TableRow, TableCell, Paper, IconButton, Chip } from '@material-ui/core';
import Icon from '@material-ui/core/Icon';
import InputFeild from "../../global/form/input"
import SelectList from "../../global/form/select"
import GoogleAddress from "../../global/google-address";
import ConfirmAction from "../../global/confirm-action";
import ApiEndpoints from "../../../constants/api_endpoints";
import store from "../../../store";
import { setError, setSuccess, setLoader } from "../../../actions/action-creators";
import { Circle, Map, Marker } from "google-maps-react";

// location_type (CITY/PINCODE/AREA) is a display/organizational label.
// lat/long/range are the actual geofence data (center + radius in km) —
// same GoogleAddress + Circle pattern job_location already uses in
// manage_location.js, reused here since lead-mapping needs real
// coordinates, not just text.
//
// Code identifiers (class/methods/state/API namespace) stay "JobFieldLocation"
// to match the backend's job_field_location naming — only the strings a
// human actually sees say "Project", matching how the rest of the webapp UI
// already calls this entity "Project" (Manage Project, Project Info, etc.)
// even though the underlying prop/data is still named `job`.
const LOCATION_TYPES = [
    { value: "CITY", label: "City" },
    { value: "PINCODE", label: "Pincode" },
    { value: "AREA", label: "Area" },
];

const formatLocation = (location) => {
    if (location.location_type === "CITY") return location.city;
    if (location.location_type === "PINCODE") return `Pincode ${location.zip}, ${location.city}`;
    if (location.location_type === "AREA") return `${location.area_name}, Pincode ${location.zip}, ${location.city}`;
    return "-";
}

const typeLabel = (location_type) => (LOCATION_TYPES.find(t => t.value === location_type) || {}).label || location_type;

const degToRad = (deg) => deg * Math.PI / 180;
const radToDeg = (rad) => rad * 180 / Math.PI;
const distanceKm = (lat1, lng1, lat2, lng2) => {
    const radlat1 = Math.PI * lat1 / 180;
    const radlat2 = Math.PI * lat2 / 180;
    const radtheta = Math.PI * (lng1 - lng2) / 180;
    let dist = Math.sin(radlat1) * Math.sin(radlat2) + Math.cos(radlat1) * Math.cos(radlat2) * Math.cos(radtheta);
    dist = Math.acos(Math.min(1, Math.max(-1, dist))) * 180 / Math.PI * 60 * 1.1515 * 1.609344;
    return dist;
}
const getMapCenterAndZoom = (points) => {
    if (!points.length) return { center: { lat: 19.0760, lng: 72.8777 }, zoom: 11 };
    let sumX = 0, sumY = 0, sumZ = 0;
    points.forEach(p => {
        const lat = degToRad(p.lat), lng = degToRad(p.lng);
        sumX += Math.cos(lat) * Math.cos(lng);
        sumY += Math.cos(lat) * Math.sin(lng);
        sumZ += Math.sin(lat);
    });
    const avgX = sumX / points.length, avgY = sumY / points.length, avgZ = sumZ / points.length;
    const lng = Math.atan2(avgY, avgX);
    const lat = Math.atan2(avgZ, Math.sqrt(avgX * avgX + avgY * avgY));
    const center = { lat: radToDeg(lat), lng: radToDeg(lng) };
    const maxDist = Math.max(...points.map(p => distanceKm(center.lat, center.lng, p.lat, p.lng)), 1);
    const zoom = Math.min(14, Math.max(3, parseInt(2000 / maxDist)));
    return { center, zoom };
}

class ManageJobFieldLocations extends Component {
    constructor(props) {
        super(props);
        this.state = {
            jobFieldLocations: [],
            isOpenAdd: false,
            newLocation: {},
            editLocationId: null,
            editLocation: {},
            isOpenConfirm: false,
            isShowMap: false,
            leads: [],
            leadStartDate: "",
            leadEndDate: "",
        }
    }

    componentDidMount() {
        this.fetchJobFieldLocations();
        this.fetchLeads();
    }

    fetchJobFieldLocations = async () => {
        store.dispatch(setLoader(true));
        try {
            let resp = await ApiEndpoints.JobFieldLocation.fetch({ job_id: this.props.job.job_id });
            this.setState({ jobFieldLocations: resp.data.data });
        }
        catch (e) {
            store.dispatch(setError({ show: true, message: e.response ? e.response.data.message : e.toString() }))
        }
        store.dispatch(setLoader(false))
    }

    fetchLeads = async () => {
        try {
            let { leadStartDate, leadEndDate } = this.state;
            let params = { job_id: this.props.job.job_id };
            if (leadStartDate) params.start_date = leadStartDate;
            if (leadEndDate) params.end_date = leadEndDate;
            let resp = await ApiEndpoints.FieldSurveyLead.leadsWithLocationByJob(params);
            this.setState({ leads: resp.data.data });
        }
        catch (e) {
            store.dispatch(setError({ show: true, message: e.response ? e.response.data.message : e.toString() }))
        }
    }

    leadsCountForLocation = (job_field_location_id) => this.state.leads.filter(lead => lead.job_field_location_id === job_field_location_id).length

    addJobFieldLocation = async (e) => {
        e.preventDefault();
        let { newLocation } = this.state;
        store.dispatch(setLoader(true));
        try {
            await ApiEndpoints.JobFieldLocation.add({ ...newLocation, job_id: this.props.job.job_id });
            await this.fetchJobFieldLocations();
            this.setState({ isOpenAdd: false, newLocation: {} });
            store.dispatch(setSuccess({ show: true, message: "Project Field Location added!" }))
        }
        catch (e) {
            store.dispatch(setError({ show: true, message: e.response ? e.response.data.message : e.toString() }))
        }
        store.dispatch(setLoader(false))
    }

    startEdit = (location) => {
        this.setState({ editLocationId: location.job_field_location_id, editLocation: { ...location } });
    }

    saveEdit = async (e) => {
        e.preventDefault();
        let { editLocationId, editLocation } = this.state;
        store.dispatch(setLoader(true));
        try {
            await ApiEndpoints.JobFieldLocation.edit({ ...editLocation, job_field_location_id: editLocationId });
            await this.fetchJobFieldLocations();
            this.setState({ editLocationId: null, editLocation: {} });
            store.dispatch(setSuccess({ show: true, message: "Project Field Location updated!" }))
        }
        catch (e) {
            store.dispatch(setError({ show: true, message: e.response ? e.response.data.message : e.toString() }))
        }
        store.dispatch(setLoader(false))
    }

    deleteJobFieldLocation = async (job_field_location_id) => {
        store.dispatch(setLoader(true));
        try {
            await ApiEndpoints.JobFieldLocation.inactive({ job_field_location_id });
            await this.fetchJobFieldLocations();
            this.setState({ isOpenConfirm: false });
        }
        catch (e) {
            store.dispatch(setError({ show: true, message: e.response ? e.response.data.message : e.toString() }))
        }
        store.dispatch(setLoader(false))
    }

    // Spread across 3 rows so nothing feels cramped:
    // Row 1: Location Title + Type
    // Row 2: Address (wide, geofence center) + Range
    // Row 3: City + type-specific fields (Pincode for PINCODE/AREA, Area Name for AREA)
    renderLocationFields = (location, onChange) => (
        <>
            <Row className="align-items-center mb-3">
                <Col xs={7}>
                    <InputFeild required placeholder="Location Title" value={location.location_title || ""} onChange={(e) => onChange({ location_title: e.target.value })} />
                </Col>
                <Col xs={4}>
                    <SelectList placeholder="Location Type" value={location.location_type || ""} onChange={(e) => onChange({ location_type: e.target.value })}>
                        {LOCATION_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                    </SelectList>
                </Col>
            </Row>
            <Row className="align-items-center mb-3">
                <Col xs={9}>
                    <GoogleAddress
                        defaultValue={location.address}
                        placeholder="Search address to set geofence center"
                        onPlaceChange={(place) => onChange({ address: place.address, city: place.city, zip: place.zip, lat: place.lat, long: place.long })}
                    />
                </Col>
                <Col xs={2}>
                    <InputFeild required type="number" placeholder="Range (Km)" value={location.range || ""} onChange={(e) => onChange({ range: e.target.value })} />
                </Col>
            </Row>
            <Row className="align-items-center">
                <Col xs={3}>
                    <InputFeild required placeholder="City" value={location.city || ""} onChange={(e) => onChange({ city: e.target.value })} />
                </Col>
                {(location.location_type === "PINCODE" || location.location_type === "AREA") &&
                    <Col xs={3}>
                        <InputFeild required placeholder="Pincode" value={location.zip || ""} onChange={(e) => onChange({ zip: e.target.value })} />
                    </Col>
                }
                {location.location_type === "AREA" &&
                    <Col xs={3}>
                        <InputFeild required placeholder="Area / Market Name" value={location.area_name || ""} onChange={(e) => onChange({ area_name: e.target.value })} />
                    </Col>
                }
            </Row>
        </>
    )

    render() {
        let { jobFieldLocations, isOpenAdd, newLocation, editLocationId, editLocation, isShowMap, leads, leadStartDate, leadEndDate } = this.state;
        const { center: mapCenter, zoom: mapZoom } = getMapCenterAndZoom([
            ...jobFieldLocations.map(loc => ({ lat: parseFloat(loc.lat), lng: parseFloat(loc.long) })),
            ...leads.map(lead => ({ lat: parseFloat(lead.lat), lng: parseFloat(lead.lng) }))
        ].filter(p => !isNaN(p.lat) && !isNaN(p.lng)));
        return (
            <div>
                <Row className="align-items-center mb-3">
                    <Col xs="auto">
                        <RSButton className="buttonred" style={{ height: "30px", paddingTop: "4px" }} onClick={() => this.setState({ isOpenAdd: true })}>
                            <Icon className="align-middle mr-1" style={{ fontSize: 16 }}>add_location</Icon> Add Project Field Location
                        </RSButton>
                    </Col>
                    <Col xs="auto" className="ml-auto">
                        <Icon style={{ cursor: "pointer" }} onClick={() => this.setState({ isShowMap: !isShowMap })}>map</Icon>
                    </Col>
                </Row>
                {isShowMap &&
                    <Row className="align-items-center mb-2 justify-content-center">
                        <Col xs="auto">
                            <InputFeild type="date" placeholder="Leads From" value={leadStartDate} onChange={(e) => this.setState({ leadStartDate: e.target.value }, this.fetchLeads)} />
                        </Col>
                        <Col xs="auto">
                            <InputFeild type="date" placeholder="Leads To" value={leadEndDate} onChange={(e) => this.setState({ leadEndDate: e.target.value }, this.fetchLeads)} />
                        </Col>
                        {(leadStartDate || leadEndDate) &&
                            <Col xs="auto">
                                <RSButton type="button" color="secondary" size="sm" onClick={() => this.setState({ leadStartDate: "", leadEndDate: "" }, this.fetchLeads)}>Clear</RSButton>
                            </Col>
                        }
                    </Row>
                }
                {isShowMap &&
                    <div style={{ height: 320 }} className="text-center mb-3">
                        <Map initialCenter={mapCenter} google={window.google} style={{ width: "60%", height: 300, left: "20%" }} zoom={mapZoom}>
                            {jobFieldLocations.map(loc =>
                                <Circle
                                    key={loc.job_field_location_id}
                                    radius={loc.range * 1000}
                                    center={{ lat: parseFloat(loc.lat), lng: parseFloat(loc.long) }}
                                    strokeColor='transparent'
                                    strokeOpacity={0}
                                    fillColor={'#f1061c'}
                                    fillOpacity={0.35}
                                />
                            )}
                            {jobFieldLocations.map(loc =>
                                <Marker
                                    key={"loc-" + loc.job_field_location_id}
                                    position={{ lat: parseFloat(loc.lat), lng: parseFloat(loc.long) }}
                                    title={loc.location_title + " (" + loc.range + " km radius)"}
                                    icon={{
                                        url: "/pin_red.svg",
                                        scaledSize: new window.google.maps.Size(32, 32)
                                    }}
                                />
                            )}
                            {leads.map(lead =>
                                <Marker
                                    key={"lead-" + lead.form_response_id}
                                    position={{ lat: parseFloat(lead.lat), lng: parseFloat(lead.lng) }}
                                    title="Lead"
                                    icon={{
                                        url: "/pin_green.svg",
                                        scaledSize: new window.google.maps.Size(24, 24)
                                    }}
                                />
                            )}
                        </Map>
                    </div>
                }
                <RSCollapse isOpen={isOpenAdd}>
                    <Card className="mb-3 shadow-sm">
                        <CardBody>
                            <form onSubmit={this.addJobFieldLocation}>
                                {this.renderLocationFields(newLocation, (patch) => this.setState({ newLocation: { ...newLocation, ...patch } }))}
                                <Row className="justify-content-end mt-3">
                                    <Col xs="auto">
                                        <RSButton className="buttonred mr-2" style={{ height: "30px", paddingTop: "4px" }}>Save</RSButton>
                                        <RSButton onClick={() => this.setState({ isOpenAdd: false, newLocation: {} })} type="button" color="secondary" style={{ height: "30px", paddingTop: "4px" }}>Cancel</RSButton>
                                    </Col>
                                </Row>
                            </form>
                        </CardBody>
                    </Card>
                </RSCollapse>

                <Paper elevation={1}>
                    <Table size="small">
                        <TableHead>
                            <TableRow style={{ background: "#fafafa" }}>
                                <TableCell style={{ fontWeight: 600 }}>Title</TableCell>
                                <TableCell style={{ fontWeight: 600, width: 100 }}>Type</TableCell>
                                <TableCell style={{ fontWeight: 600 }}>Coverage</TableCell>
                                <TableCell style={{ fontWeight: 600 }}>Geofence</TableCell>
                                <TableCell style={{ fontWeight: 600 }} align="right">Leads</TableCell>
                                <TableCell style={{ fontWeight: 600 }} align="right">Action</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {!jobFieldLocations.length ?
                                <TableRow>
                                    <TableCell colSpan={6} align="center">
                                        <em style={{ color: "#999" }}>No Project Field Locations added yet</em>
                                    </TableCell>
                                </TableRow>
                                :
                                jobFieldLocations.map(location =>
                                    editLocationId === location.job_field_location_id ?
                                        <TableRow key={location.job_field_location_id}>
                                            <TableCell colSpan={6}>
                                                <form onSubmit={this.saveEdit}>
                                                    {this.renderLocationFields(editLocation, (patch) => this.setState({ editLocation: { ...editLocation, ...patch } }))}
                                                    <Row className="justify-content-end mt-3">
                                                        <Col xs="auto">
                                                            <IconButton size="small" type="submit"><Icon style={{ fontSize: 18 }}>done</Icon></IconButton>
                                                            <IconButton size="small" type="button" onClick={() => this.setState({ editLocationId: null, editLocation: {} })}><Icon style={{ fontSize: 18 }}>close</Icon></IconButton>
                                                        </Col>
                                                    </Row>
                                                </form>
                                            </TableCell>
                                        </TableRow>
                                        :
                                        <TableRow key={location.job_field_location_id} hover>
                                            <TableCell>{location.location_title}</TableCell>
                                            <TableCell><Chip size="small" label={typeLabel(location.location_type)} /></TableCell>
                                            <TableCell>{formatLocation(location)}</TableCell>
                                            <TableCell>
                                                <span className="d-block" style={{ fontSize: 11 }}>{parseFloat(location.lat).toFixed(4)}, {parseFloat(location.long).toFixed(4)}</span>
                                                <span className="d-block text-muted" style={{ fontSize: 11 }}>{location.range} km radius</span>
                                            </TableCell>
                                            <TableCell align="right">{this.leadsCountForLocation(location.job_field_location_id)}</TableCell>
                                            <TableCell align="right">
                                                <IconButton size="small" onClick={() => this.startEdit(location)}><Icon style={{ fontSize: 18 }}>edit</Icon></IconButton>
                                                <IconButton size="small" onClick={() => this.setState({ isOpenConfirm: () => this.deleteJobFieldLocation(location.job_field_location_id) })}><Icon style={{ fontSize: 18 }}>delete</Icon></IconButton>
                                            </TableCell>
                                        </TableRow>
                                )
                            }
                        </TableBody>
                    </Table>
                </Paper>
                <ConfirmAction isOpen={!!this.state.isOpenConfirm} callback={this.state.isOpenConfirm} onClose={() => this.setState({ isOpenConfirm: false })} />
            </div>
        );
    }
}
export default ManageJobFieldLocations;
